import firebase from 'firebase'
import store from '@/store'
import lodash from 'lodash'
import { isWithinInterval } from 'date-fns'
import { workspaceAccess } from './workspace'
import WorkspaceNotification from '@/helpers/email-templates/workspaceNotification.js'
import { getBaseURL, WorkspaceRoleChangedEmail } from '@/helpers/emails'
import { getWorkspaceUrl } from '@/utils/workspaceUrl'
import {
  categories,
  domains,
  advertisers,
  profile,
  posts,
  promotors,
  shared_access,
  groups,
  groupMembers,
  groupDomains
} from '../firebase/collections'
import RSSParser from 'rss-parser'

const extractDomainFromUrl = url => {
  if (typeof url !== 'string') return null

  const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^/?#]+)(?:[/?#]|$)/i)
  const domain = match && match[1]
  return domain ? domain.replace(/^www\./i, '') : null
}

const getAuthenticatedHeaders = async () => {
  const user = firebase.auth().currentUser
  if (!user) {
    throw new Error('You must be logged in to use this feature')
  }

  const idToken = await user.getIdToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  }
}

const authenticatedRequest = async (path, options = {}) => {
  const headers = {
    ...(await getAuthenticatedHeaders()),
    ...(options.headers || {})
  }

  const apiBase = (process.env.VUE_APP_API || '').replace(/\/+$/, '')
  const normalisedPath = path.replace(/^\/+/, '')
  const response = await fetch(`${apiBase}/${normalisedPath}`, {
    ...options,
    headers
  })

  let data = {}
  try {
    data = await response.json()
  } catch {
    data = {}
  }

  if (!response.ok) {
    const error = new Error(
      data.error || 'The request could not be completed. Please try again.'
    )
    error.code = data.code
    error.response = {
      data,
      status: response.status
    }
    throw error
  }

  return data
}

export default {
  is_url(urlString) {
    try {
      return Boolean(new URL(urlString))
    } catch (e) {
      return false
    }
  },

  async uploadBase64Image({ base64, path }) {
    try {
      const metaData = {
        contentType: 'image/png'
      }
      const storageRef = firebase.storage().ref()
      // Get a reference to the existing path without attempting to create it
      const imageRef = storageRef.child(path)
      // Upload the image
      await imageRef.putString(base64, 'base64', metaData)
      // Get the download URL
      const downloadUrl = await imageRef.getDownloadURL()
      return downloadUrl
    } catch (error) {
      return null
    }
  },

  async checkFirebaseStoragePath(path) {
    try {
      // Reference to the Firebase Storage
      const storageRef = firebase.storage().ref()
      // List items under the specified path
      const pathRef = storageRef.child(path)
      const storagePathCheck = pathRef
        .getMetadata()
        .then(() => {
          return true
        })
        .catch(() => {
          return false
        })
      return storagePathCheck
    } catch {
      return false // Indicate failure
    }
  },

  async getFirebaseStoragePath(path) {
    try {
      const storageRef = firebase.storage().ref()
      const imageRef = storageRef.child(path)
      const downloadUrl = await imageRef.getDownloadURL()
      return downloadUrl
    } catch (error) {
      return null
    }
  },

  async getFastMetadata(url) {
    try {
      const res = await fetch(`${process.env.VUE_APP_API}getMetadata`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url })
      })

      if (!res.ok) {
        throw new Error('Failed to fetch metadata')
      }

      const data = await res.json()
      return data
    } catch (err) {
      return {
        success: false,
        shouldFallback: true,
        error: err?.message || 'Failed to fetch metadata'
      }
    }
  },

  async saveOpenRouterApiKey(apiKey) {
    return authenticatedRequest('/byok/openrouter', {
      method: 'POST',
      body: JSON.stringify({ apiKey })
    })
  },

  async getOpenRouterKeyStatus() {
    return authenticatedRequest('/byok/openrouter', {
      method: 'GET'
    })
  },

  async getOpenRouterModels() {
    return authenticatedRequest('/byok/openrouter/models', {
      method: 'GET'
    })
  },

  async saveOpenRouterModel(model) {
    return authenticatedRequest('/byok/openrouter/model', {
      method: 'POST',
      body: JSON.stringify({ model })
    })
  },

  async removeOpenRouterApiKey() {
    return authenticatedRequest('/byok/openrouter', {
      method: 'DELETE'
    })
  },

  async aiRequest(path, body = {}) {
    return authenticatedRequest(path, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  },

  async sendEmail(config) {
    const response = await fetch(`${process.env.VUE_APP_API}email`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ config })
    })

    let result = {}
    try {
      result = await response.json()
    } catch {
      // Keep the HTTP status as the source of truth if the response is not JSON.
    }

    if (!response.ok || result.success === false) {
      const error = new Error(result.message || 'Failed to send email')
      error.details = result.error
      throw error
    }

    return result
  },

  async notifyMembersNewLink(groupToken, linkData, addedByUser) {
    try {
      // Get group information
      const groupDoc = await groups.doc(groupToken).get()
      if (!groupDoc.exists) {
        throw new Error('Group not found')
      }

      const groupData = groupDoc.data()
      const groupName = groupData.groupName || groupData.name

      // Get all active group members
      const membersSnapshot = await groupMembers
        .doc(groupToken)
        .collection('members')
        .where('status', '==', 'active')
        .get()

      if (membersSnapshot.empty) {
        return
      }

      // Get member details and filter out the user who added the link
      const memberPromises = membersSnapshot.docs
        .filter(memberDoc => memberDoc.id !== addedByUser.uid)
        .map(async memberDoc => {
          const memberData = memberDoc.data()
          if (memberData.email) {
            return memberData.email
          }
          // If email not stored in member data, get from user profile
          try {
            const userProfileDoc = await profile.doc(memberDoc.id).get()
            if (userProfileDoc.exists) {
              const userProfile = userProfileDoc.data()
              // Check email preferences
              const emailPrefs = userProfile.settings?.emailPreferences
              const allowWorkspaceNotifications =
                emailPrefs?.workspaceNotifications !== false

              if (allowWorkspaceNotifications && userProfile.email) {
                return userProfile.email
              }
            }
          } catch {
            // Error getting profile for member
          }
          return null
        })

      const memberEmails = (await Promise.all(memberPromises)).filter(
        email => email
      )

      if (memberEmails.length === 0) {
        return
      }

      // Generate email content using the workspace notification template
      const workspaceUrl = `${getBaseURL()}/teams/workspace`
      const addedByName =
        addedByUser.displayName || addedByUser.email || 'A team member'

      // Use the imported workspace notification template with group token
      const emailHtml = await WorkspaceNotification(
        groupName,
        linkData,
        addedByName,
        workspaceUrl,
        getBaseURL(),
        groupToken
      )

      // Send emails to all members
      const emailPromises = memberEmails.map(email =>
        this.sendEmail({
          email: email,
          subject: `New link added to ${groupName} workspace`,
          html: emailHtml
        })
      )

      await Promise.all(emailPromises)
    } catch {
      // Don't throw error to prevent breaking the main flow
    }
  },

  async notifyWorkspaceMemberRoleChanged(
    groupToken,
    member,
    previousRole,
    newRole
  ) {
    if (
      !member?.uid ||
      member.isPending ||
      previousRole === newRole
    ) {
      return
    }

    try {
      const groupDoc = await groups.doc(groupToken).get()
      if (!groupDoc.exists) return

      const groupData = groupDoc.data()
      let memberProfile = {}
      try {
        const memberProfileDoc = await profile.doc(member.uid).get()
        memberProfile = memberProfileDoc.exists ? memberProfileDoc.data() : {}
      } catch {
        // Profile access is optional; it must not prevent role notifications.
      }

      if (
        memberProfile.settings?.emailPreferences?.workspaceNotifications ===
        false
      ) {
        return
      }

      const recipientEmail = member.email || memberProfile.email
      if (!recipientEmail) return

      const groupName = groupData.groupName || groupData.name || 'workspace'
      const workspacePath = getWorkspaceUrl({ ...groupData, token: groupToken })
      const workspaceLink = `${getBaseURL()}${workspacePath}?group=${encodeURIComponent(
        groupToken
      )}`
      const emailConfig = await WorkspaceRoleChangedEmail({
        groupName,
        previousRole,
        newRole,
        workspaceLink,
        memberName: member.displayName
      })

      const sendResult = await this.sendEmail({
        email: recipientEmail,
        subject: emailConfig.subject,
        html: emailConfig.html
      })

      if (sendResult?.success !== true) {
        throw new Error('Email service did not confirm delivery')
      }
      return true
    } catch (error) {
      // Role changes should succeed even if the notification email cannot be sent.
      console.error('Error sending workspace role notification:', error)
      return false
    }
  },

  profileUpdate: async value => {
    try {
      const user = firebase.auth().currentUser

      if (!user) {
        throw new Error('User is not authenticated')
      }

      const { uid, providerData } = user
      const dateUpdated = firebase.firestore.Timestamp.fromDate(new Date())

      const profileUpdate = {
        ...providerData[0],
        ...value,
        date_updated: dateUpdated,
        user_id: uid
      }

      // update firebase profile
      await user.updateProfile(profileUpdate)

      // update Firebase firestore database profile
      await profile.doc(uid).set(profileUpdate, { merge: true })

      const existingProfile = store.state.auth.profile || {}
      store.commit('setProfile', {
        ...existingProfile,
        ...profileUpdate,
        settings: {
          ...(existingProfile.settings || {}),
          ...(profileUpdate.settings || {})
        }
      })

      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  },

  checkDateExpirey: item => {
    const checkExpirey = isWithinInterval(new Date(), {
      start: item.approvedDate.toDate(),
      end: item.expiredDate.toDate()
    }) // => true if todays date is below the future date
    return checkExpirey
  },

  addAdvert: (state, value) => {
    value.user_id = firebase.auth().currentUser.uid
    firebase.auth().currentUser.uid
      ? advertisers
          .doc('starter')
          .collection('adverts')
          .add(value)
      : null
  },

  updateAdvert: (state, value) => {
    firebase.auth().currentUser.uid
      ? advertisers
          .doc('starter')
          .collection('adverts')
          .doc(value.doc_id)
          .update(value)
      : null
  },

  fetchApprovedAdverts: ({ commit }) => {
    // Return the unsubscribe function so it can be stored and called later
    const unsubscribe = advertisers
      .doc('starter')
      .collection('adverts')
      .onSnapshot(
        querySnapshot => {
          let array = []
          querySnapshot.forEach(doc => {
            //doc.data() && doc.data().expiredDate ? array.push(doc.data()) : []
            doc.data() ? array.push(doc.data()) : []
          })

          // const newArray = array.map(i => {
          // const checkExpirey = isWithinInterval(new Date(), {
          //   start: i.approvedDate.toDate(),
          //   end: i.expiredDate.toDate()
          // })
          //i.status = checkExpirey
          // checkExpirey
          //   ? advertisers
          //       .doc('starter')
          //       .collection('adverts')
          //       .doc(i.doc_id)
          //       .set({
          //         ...i,
          //         status: checkExpirey
          //       })
          //   : null
          //   return i
          // })

          const approvedAdverts = array.filter(e => e.status === true)

          commit('setAdverts', approvedAdverts)
        },
        () => {
          // Error response
        }
      )

    return unsubscribe
  },

  fetchCategories: ({ commit }) => {
    categories.get().then(
      snapshot => {
        snapshot.forEach(doc => {
          commit('setCategories', doc.data())
        })
      },
      error => {
        console.log('Error fetching categories:', error)
      }
    )
  },

  // Fetching workspace links
  fetchTeamsWorkspaceLinks({ commit }, group) {
    const currentUser = firebase.auth().currentUser

    // For public workspaces, allow access without authentication
    const isPublicWorkspace =
      group.settings?.isPrivate === false || group.isPublic === true

    if (!currentUser?.uid && !isPublicWorkspace) {
      // Private workspace requires authentication
      return
    }

    return groupDomains
      .doc(group.token || group.id)
      .collection('categories')
      .get()
      .then(querySnapshot => {
        let categories = []
        querySnapshot.forEach(doc => {
          const data = doc.data()
          categories.push({
            ...data,
            category: data.category
          })
        })

        if (categories.length > 0) {
          let groupedCategories = lodash.mapValues(
            lodash.groupBy(categories, 'category'),
            categories =>
              categories.map(category => ({
                ...category
              }))
          )
          commit('setTeamsWorkspaceLinks', groupedCategories)
        } else {
          commit('setTeamsWorkspaceLinks', [])
        }
      })
      .catch(error => {
        console.error('Error fetching workspace links:', error)
        commit('setTeamsWorkspaceLinks', [])
      })
  },

  fetchLinks: ({ commit }) => {
    if (!firebase.auth().currentUser?.uid) {
      return null
    }

    // Return the unsubscribe function so it can be stored and called later
    const unsubscribe = domains
      .doc(firebase.auth().currentUser.uid)
      .collection('categories')
      .onSnapshot(
        querySnapshot => {
          let categories = []
          querySnapshot.forEach(function(doc) {
            const data = doc.data()
            categories.push({
              ...data,
              doc_id: data.doc_id || doc.id,
              domain:
                data.domain || extractDomainFromUrl(data.link || data.url) || ''
            })
          })

          if (categories) {
            commit('setSearchLinks', categories)
            let groupedCategories = lodash.mapValues(
              lodash.groupBy(categories, 'category'),
              d => d.map(category => lodash.omit(category, 'category'))
            )

            if (groupedCategories) commit('setLinks', groupedCategories)
          }
        },
        error => {
          console.log('Error fetching links:', error)
        }
      )

    return unsubscribe
  },

  fetchFavourites: ({ commit }) => {
    if (!firebase.auth().currentUser?.uid) {
      return null
    }

    // Return the unsubscribe function so it can be stored and called later
    const unsubscribe = domains
      .doc(firebase.auth().currentUser.uid)
      .collection('categories')
      .onSnapshot(
        querySnapshot => {
          let favourites = []
          querySnapshot.forEach(doc => {
            doc.data().favourite ? favourites.push(doc.data()) : null
          })

          if (favourites) commit('setFavourites', favourites)
        },
        error => {
          console.log('Error fetching favourites:', error)
        }
      )

    return unsubscribe
  },

  extractDomain: url => {
    return extractDomainFromUrl(url)
  },

  addLink: value => {
    return new Promise((resolve, reject) => {
      if (!firebase.auth().currentUser?.uid) {
        reject(new Error('User not authenticated'))
        return
      }

      const userDocRef = domains.doc(firebase.auth().currentUser.uid)

      // Check if link already exists for this user (regardless of category)
      userDocRef
        .collection('categories')
        .where('link', '==', value.link)
        .get()
        .then(querySnapshot => {
          if (!querySnapshot.empty) {
            // Link already exists
            reject(
              new Error(`This link has already been added to your bookmarks`)
            )
            return
          }

          // If no duplicate found, continue adding the link
          const newLinkRef = userDocRef.collection('categories').doc()
          const doc_id = newLinkRef.id

          const now = firebase.firestore.Timestamp.fromDate(new Date())
          const linkData = {
            ...value,
            user_id: firebase.auth().currentUser.uid,
            date_created: value.date_created || now,
            date_updated: now,
            doc_id: doc_id
          }

          newLinkRef
            .set(linkData)
            .then(() => {
              resolve(linkData)
            })
            .catch(error => {
              reject(error)
            })
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  editLink: (state, value) => {
    value.category = value.categoryTitle
    value.date_updated = firebase.firestore.Timestamp.fromDate(new Date())

    firebase.auth().currentUser?.uid
      ? domains
          .doc(firebase.auth().currentUser.uid)
          .collection('categories')
          .doc(value.doc_id)
          .set(value)
      : null
  },

  bulkUpdateCategory: (docIds, newCategory) => {
    return new Promise((resolve, reject) => {
      if (!firebase.auth().currentUser?.uid) {
        reject(new Error('User not authenticated'))
        return
      }

      if (!docIds || docIds.length === 0) {
        reject(new Error('No items selected'))
        return
      }

      if (!newCategory) {
        reject(new Error('New category name is required'))
        return
      }

      const userId = firebase.auth().currentUser.uid
      const batch = firebase.firestore().batch()
      const timestamp = firebase.firestore.Timestamp.fromDate(new Date())

      // Update each document in the batch
      docIds.forEach(docId => {
        const docRef = domains
          .doc(userId)
          .collection('categories')
          .doc(docId)

        batch.update(docRef, {
          category: newCategory,
          categoryTitle: newCategory,
          date_updated: timestamp
        })
      })

      // Commit the batch
      batch
        .commit()
        .then(() => {
          resolve({
            success: true,
            count: docIds.length,
            newCategory: newCategory
          })
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  addFavourite: (state, value) => {
    firebase.auth().currentUser?.uid
      ? domains
          .doc(firebase.auth().currentUser.uid)
          .collection('categories')
          .doc(value.doc_id)
          .set(value)
      : null
  },

  addPost: (state, value) => {
    value.user_id = firebase.auth().currentUser?.uid
    value.date_created = firebase.firestore.Timestamp.fromDate(new Date())

    firebase.auth().currentUser?.uid
      ? posts
          .doc(firebase.auth().currentUser.uid)
          .collection('news')
          .add(value)
      : null
  },

  async getPosts(advert_user_id) {
    if (firebase.auth().currentUser?.uid && advert_user_id) {
      const querySnapshot = await posts
        .doc(advert_user_id)
        .collection('news')
        .get()
      const Posts = []

      querySnapshot.forEach(doc => {
        const post = doc.data()
        Posts.push(post)
      })

      // Sort the Posts array by date_created in descending order (latest at the top)
      Posts.sort((a, b) => b.date_created.seconds - a.date_created.seconds)

      return Posts
    }
  },

  addFollower: (connect_uid, followerAddedCallback = undefined) => {
    return new Promise((resolve, reject) => {
      const user_id = firebase.auth().currentUser?.uid
      const followersCollection = promotors
        .doc(connect_uid)
        .collection('followers')

      // Check if the user is authenticated
      if (user_id) {
        // Add a new follower with a unique ID
        followersCollection
          .add({
            user_id,
            date_created: firebase.firestore.Timestamp.fromDate(new Date())
          })
          .then(() => {
            resolve() // Resolve the Promise when the follower is successfully added
            if (followerAddedCallback) {
              followerAddedCallback() // Call the callback function if it's provided
            }
          })
          .catch(error => {
            reject(error) // Reject the Promise if adding the follower fails
          })
      } else {
        reject('User not authenticated')
      }
    })
  },

  async getFollowers(advert_user_id) {
    if (!advert_user_id) {
      throw new Error('advert_user_id is required')
    }

    // Check if the advert_user_id document exists
    const advertUserDoc = await promotors
      .doc(advert_user_id)
      .collection('followers')
      .get()

    // Get the number of documents in the followers collection
    return advertUserDoc.size
  },

  unFollow: connect_uid => {
    return new Promise((resolve, reject) => {
      const user_id = firebase.auth().currentUser?.uid

      // Check if the user is authenticated
      if (user_id) {
        // Query to find the follower document with the matching user_id
        promotors
          .doc(connect_uid)
          .collection('followers')
          .where('user_id', '==', user_id)
          .get()
          .then(querySnapshot => {
            // Check if any matching documents were found
            if (!querySnapshot.empty) {
              // Get the first document (assuming there's only one)
              const followerDoc = querySnapshot.docs[0]

              // Delete the follower document
              followerDoc.ref
                .delete()
                .then(() => {
                  resolve() // Resolve the Promise when deletion is successful
                })
                .catch(error => {
                  reject(error) // Reject the Promise if deletion fails
                })
            } else {
              reject('No matching follower found')
            }
          })
          .catch(error => {
            reject(error)
          })
      } else {
        reject('User not authenticated')
      }
    })
  },

  async isFollowing(advert_user_id) {
    if (firebase.auth().currentUser?.uid && advert_user_id) {
      const querySnapshot = await promotors
        .doc(advert_user_id)
        .collection('followers')
        .get()

      let isFollowing = false // Initialize a variable to track the result

      querySnapshot.forEach(doc => {
        const user_id = doc.data().user_id
        if (user_id === firebase.auth().currentUser.uid) {
          isFollowing = true // Set isFollowing to true if a match is found
        }
      })

      return isFollowing // Return the result after the loop
    }
    return false // Return false if conditions aren't met
  },

  deleteLink: (state, value) => {
    firebase.auth().currentUser?.uid
      ? domains
          .doc(firebase.auth().currentUser.uid)
          .collection('categories')
          .doc(value.doc_id)
          .delete()
          .catch(function() {
            // Error removing document
          })
      : null
  },

  // BEGIN PERMISSIONS
  // User permissions Store token in auth > workspace > token
  // This identifies which users share an admins workspace
  sharedAccess: async uid => {
    try {
      const admin_user_id = firebase.auth().currentUser?.uid

      if (!uid) {
        throw new Error('Current user not authenticated')
      }
      if (admin_user_id) {
        const sharedAccessRef = shared_access.doc(admin_user_id)
        const doc = await sharedAccessRef.get()
        const existingAccessList = doc.exists ? doc.data().accessList || [] : []

        if (!existingAccessList.includes(uid)) {
          existingAccessList.push(uid)
          await sharedAccessRef.set({ accessList: existingAccessList })
        }
      }
    } catch {
      // Error updating access list
    }
  },

  async checkIfGroupExists(groupNameOrToken, isAssignMode = true) {
    const currentUser = firebase.auth().currentUser
    if (!currentUser) {
      return { status: false, group: null }
    }

    try {
      let groupDoc

      if (isAssignMode) {
        // When creating new group, check by name
        const groupsSnapshot = await groups
          .where('ownerId', '==', currentUser.uid)
          .where('name', '==', groupNameOrToken)
          .get()

        if (!groupsSnapshot.empty) {
          groupDoc = groupsSnapshot.docs[0]
        }
      } else {
        // When selecting existing group, check both ownership and membership
        const groupRef = await groups.doc(groupNameOrToken).get()

        if (groupRef.exists) {
          const groupData = groupRef.data()

          // Check if user is owner
          if (groupData.ownerId === currentUser.uid) {
            groupDoc = groupRef
          } else {
            // Check if user is a member
            const memberDoc = await groupMembers
              .doc(groupNameOrToken)
              .collection('members')
              .doc(currentUser.uid)
              .get()

            if (memberDoc.exists) {
              groupDoc = groupRef
            }
          }
        }
      }

      if (groupDoc) {
        return {
          status: true,
          group: {
            ...groupDoc.data(),
            token: groupDoc.id
          }
        }
      }

      return { status: false, group: null }
    } catch {
      return { status: false, group: null }
    }
  },

  async getWorkspaceGroups(groupTokens) {
    const currentUser = firebase.auth().currentUser
    if (!currentUser) {
      return []
    }

    try {
      const allGroups = []

      // Query the groups collection for each token
      const groupPromises = groupTokens.map(async token => {
        // Get the group document
        const groupDoc = await groups.doc(token).get()

        if (groupDoc.exists) {
          const groupData = groupDoc.data()

          // Check if user is owner or member
          if (groupData.ownerId === currentUser.uid) {
            allGroups.push(groupData)
          } else {
            // Check if user is a member
            const memberDoc = await groupMembers
              .doc(token)
              .collection('members')
              .doc(currentUser.uid)
              .get()

            if (memberDoc.exists) {
              allGroups.push(groupData)
            }
          }
        }
      })

      await Promise.all(groupPromises)
      return allGroups
    } catch {
      return []
    }
  },

  // Set permission for admin roles
  async setPermissions(email, permission) {
    try {
      return await authenticatedRequest('setPermissions', {
        method: 'POST',
        body: JSON.stringify({ email, permissions: permission })
      })
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },
  // END PERMISSIONS

  async getUser(config) {
    try {
      const adminUid = firebase.auth().currentUser?.uid

      if (!adminUid) {
        throw new Error('Current user not authenticated')
      }

      config.adminUid = adminUid

      const response = await fetch(`${process.env.VUE_APP_API}getUser`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config) // Pass either { email } or { uid }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Error retrieving user')
      }

      const data = await response.json()
      return data
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },

  // Workspace methods
  ...workspaceAccess,

  async fetchRssFeed(domain) {
    try {
      const response = await fetch(`${process.env.VUE_APP_API}fetchRssFeed`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ domain })
      })
      if (!response.ok) {
        throw new Error('Failed to fetch RSS feed')
      }
      const feedText = await response.text()
      const parser = new RSSParser()
      const feed = await parser.parseString(feedText)
      return {
        success: true,
        feedItems: feed.items,
        feedImage: feed.image
      }
    } catch {
      return {
        success: false,
        error: {
          type: 'error',
          title: 'RSS Feed Error',
          text: 'Unable to load RSS feed for this website'
        }
      }
    }
  },

  async generateUnsubscribeToken(email) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      const uid = currentUser.uid

      const response = await fetch(
        `${process.env.VUE_APP_API}generateUnsubscribeToken`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, uid })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.message || 'Error generating unsubscribe token'
        )
      }

      const data = await response.json()
      return data
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },

  async processUnsubscribe(token, email, preferences = null) {
    try {
      const response = await fetch(
        `${process.env.VUE_APP_API}processUnsubscribe`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token, email, preferences })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.message || 'Error processing unsubscribe request'
        )
      }

      const data = await response.json()
      return data
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },

  async sendBulkMarketingEmails(config) {
    // Only allow this for admin users
    if (!store.state?.auth?.permissions?.admin) {
      return { error: 'Unauthorized. Only admins can send bulk emails.' }
    }

    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      // Get a fresh ID token
      const idToken = await currentUser.getIdToken(true)

      // Determine if we're in a local development environment
      const isLocalEnvironment = process.env.NODE_ENV === 'development'

      // Make API request to send emails using Mailgun
      const response = await fetch(
        `${process.env.VUE_APP_API}sendBulkEmailsMailgun`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            senderUid: currentUser.uid,
            isLocalEnvironment,
            ...config
          })
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Error ${response.status}: ${errorText}`)
      }

      const data = await response.json()
      return data
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },

  async sendFeatureAnnouncementEmail(email) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      const uid = currentUser.uid

      // Check user's email preferences before sending
      const profileDoc = await firebase
        .firestore()
        .collection('profile')
        .doc(uid)
        .get()

      // If user has opted out of feature announcements, don't send the email
      if (
        profileDoc.exists &&
        profileDoc.data()?.settings?.emailPreferences?.featureAnnouncements ===
          false
      ) {
        return {
          success: false,
          message: 'User has opted out of feature announcements'
        }
      }

      const tokenResponse = await this.generateUnsubscribeToken(email)

      if (tokenResponse.error) {
        throw new Error(tokenResponse.error)
      }

      // Get the email template with tokens replaced
      const emailConfig = await import('@/helpers/emails').then(module => {
        return module.FeatureAnnouncementEmail(email, tokenResponse.token)
      })

      // Send the email
      const response = await this.sendEmail({
        email: email,
        subject: emailConfig.subject,
        html: await emailConfig.html
      })

      return response
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },

  async sendFeatureAnnouncementToAll() {
    // Only allow this for admin users
    if (!this.$store?.state?.auth?.permissions?.admin) {
      return { error: 'Unauthorized. Only admins can send bulk emails.' }
    }

    try {
      // Get all users who haven't opted out of feature announcements
      const usersSnapshot = await firebase
        .firestore()
        .collection('profile')
        .get()

      const eligibleUsers = []
      let sentCount = 0
      let skippedCount = 0

      // Process each user
      usersSnapshot.forEach(doc => {
        const userData = doc.data()

        // Check if user has opted out
        const hasOptedOut =
          userData.settings?.emailPreferences?.featureAnnouncements === false

        if (!hasOptedOut && userData.email) {
          eligibleUsers.push({
            uid: doc.id,
            email: userData.email,
            displayName: userData.displayName || 'User'
          })
        }
      })

      // Send emails in batches to avoid rate limits
      const batchSize = 10

      for (let i = 0; i < eligibleUsers.length; i += batchSize) {
        const batch = eligibleUsers.slice(i, i + batchSize)

        await Promise.all(
          batch.map(async user => {
            try {
              const result = await this.sendFeatureAnnouncementEmail(user.email)
              if (result.error) {
                skippedCount++
              } else {
                sentCount++
              }
            } catch {
              skippedCount++
            }
          })
        )

        // Wait a bit before sending the next batch to avoid overwhelming the email service
        await new Promise(resolve => setTimeout(resolve, 2000))
      }

      return {
        success: true,
        message: `Feature announcement sent to ${sentCount} users. Skipped ${skippedCount} users.`
      }
    } catch (err) {
      return { error: err?.message || 'An error occurred' }
    }
  },

  async organiseCategoriesWithAI(domains) {
    try {
      return await authenticatedRequest('/ai/organise-categories', {
        method: 'POST',
        body: JSON.stringify({ domains })
      })
    } catch (err) {
      return {
        success: false,
        error: err?.message || 'An error occurred',
        code: err?.code
      }
    }
  },

  async suggestCategoryWithAI(
    domain,
    title,
    description = '',
    existingCategories = []
  ) {
    try {
      return await authenticatedRequest('/ai/suggest-category', {
        method: 'POST',
        body: JSON.stringify({
          domain,
          title,
          description,
          existingCategories
        })
      })
    } catch (err) {
      return {
        success: false,
        error: err?.message || 'An error occurred',
        code: err?.code
      }
    }
  }
}
